import { useCallback, useEffect } from 'react';
import { FieldValues, UseFormProps, useForm } from 'react-hook-form';
import { useBlocker } from 'react-router-dom';

import { useSafeFormContext } from '@/shared/lib/SafeFormContext/SafeFormContext';

const useSafeForm = <T extends FieldValues>(props: UseFormProps<T>) => {
  const form = useForm<T>(props);
  const { showConfirmation } = useSafeFormContext();
  const blocker = useBlocker(() => form.formState.isDirty && !form.formState.isSubmitSuccessful);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      showConfirmation(
        () => blocker.proceed(),
        () => blocker.reset(),
      );
    }
  }, [blocker, form.formState.isDirty, form.formState.isSubmitSuccessful, showConfirmation]);

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (form.formState.isDirty) {
        event.preventDefault();
        event.returnValue = '';
      }
    },
    [form.formState.isDirty],
  );

  useEffect(() => {
    if (form.formState.isDirty && !form.formState.isSubmitSuccessful) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [form.formState.isDirty, form.formState.isSubmitSuccessful, handleBeforeUnload]);

  return form;
};

export default useSafeForm;
